import { getStoryBySlug, StoryData } from '@cinch-nx/data-storyblok'
import { GetStaticPropsContext } from 'next'
import { PolicyPageStoryContent } from './types/policy-page'
import { setTag, tagError } from '@core/datadog-server'
import { termsAndConditionsFallback } from './data/terms-and-conditions-fallback'
import { cookiePolicyFallback } from './data/cookie-policy-fallback'
import { privacyFallback } from './data/privacy-fallback'

export function getStaticPropsGenerator(path: string) {
  return async ({ preview = false, params }: GetStaticPropsContext) => {
    try {
      const slug = path === 'rangeclaim' ? `${path}` : `${path}/${params?.slug}`
      const story = await getStoryBySlug<PolicyPageStoryContent>(slug, {
        version: preview ? 'draft' : 'published',
      })
      if (!story) {
        throw new Error('No story found for this policy page')
      }

      setTag('page.name', slug)
      setTag('page.rendered', 'ISR')
      setTag('page.revalidate-in', '60s')
      setTag('page.params', JSON.stringify({ preview }, null, 2))

      return {
        props: {
          story,
          preview,
          layoutProps: {
            showBorderRadius: true,
          },
        },
        revalidate: 60, // In seconds
      }
    } catch (error) {
      if (error instanceof Error || typeof error === 'string') {
        tagError(error)
      }

      const storyFallbackMap: Record<
        string,
        StoryData<PolicyPageStoryContent>
      > = {
        privacy: privacyFallback,
        'cookie-policy': cookiePolicyFallback,
        'terms-and-conditions': termsAndConditionsFallback,
      }

      const fallbackStory = storyFallbackMap[params?.slug as string]

      if (fallbackStory) {
        return {
          props: {
            story: fallbackStory,
          },
        }
      }

      return {
        notFound: true,
        props: {
          story: null,
          preview,
          layoutProps: {
            showBorderRadius: true,
          },
        },
        revalidate: 60,
      }
    }
  }
}

export type PolicyPageFeatureProps = Extract<
  Awaited<ReturnType<ReturnType<typeof getStaticPropsGenerator>>>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  { props: any }
>['props']
